/**
 * From a finished article to the two things that carry it outward: a LinkedIn
 * post that points at it, and a dated row on the Publishing calendar.
 *
 * 🔴 THE POST IS DRAFTED FROM THE PUBLISHED PAGE, NEVER FROM THE TITLE.
 *
 * The composer's `url-repurposer` skill (an explicit `/repurpose` →
 * `scan_article`) reads the page and carries the
 * rule "never state a fact that is not in the scanned article". That is the
 * guardrail. An article with no public link has nothing for it to read, and a
 * prompt built from the title alone would let the model write a post about an
 * article it never saw — the invented-claim failure this pipeline exists to
 * stop, laundered through a client's byline. So no link, no draft; the reason
 * is stated beside the button rather than the button disappearing.
 *
 * Wrong law (`must_fix` claim-check items) and an article whose claims were
 * never read against a source block it too: a post inherits every claim it
 * summarises.
 */

import type { DeskContentType } from '@/lib/desk/content-type';

export interface ArticlePromotionInput {
  /** The latest non-cancelled publication's public URL, when recorded. */
  publishedUrl: string | null;
  /** Claim-check items that state the law wrongly (`must_fix`). */
  wrongLawCount: number;
  /** True when no source behind the article's claims was ever retrieved. */
  needsVerifiedRewrite: boolean;
}

/** Why a LinkedIn post cannot be drafted yet, or null when it can. */
export function articlePostBlocker(input: ArticlePromotionInput): string | null {
  if (input.wrongLawCount > 0) {
    return input.wrongLawCount === 1
      ? 'One claim states the law wrongly. Fix it before a post repeats it.'
      : `${input.wrongLawCount} claims state the law wrongly. Fix them before a post repeats them.`;
  }
  if (input.needsVerifiedRewrite) {
    return 'The claims were never checked against a source. Rewrite with verified sources first.';
  }
  if (!publicUrl(input.publishedUrl)) {
    return 'Available once the article has a public link. The post is written from the published page and links to it. Record the link in Hand off article.';
  }
  return null;
}

/** An absolute http(s) URL, or null. */
export function publicUrl(value: string | null | undefined): string | null {
  const trimmed = value?.trim();
  if (!trimmed) return null;
  try {
    const url = new URL(trimmed);
    return url.protocol === 'https:' || url.protocol === 'http:' ? url.toString() : null;
  } catch {
    return null;
  }
}

/**
 * The composer prompt. An EXPLICIT `/repurpose` (the url-repurposer skill:
 * `scan_article` reads the page first, then "never state a fact that is not in
 * the scanned article"). Not left to `detectImplicitSlash`: its self-domain
 * rule runs before the repurpose rule, so a client article published on the
 * workspace's OWN site — the normal case for ITC — would route to
 * `brand-posts`, which grounds in the brand kit and never reads the article.
 * An explicit binding is kept by `resolveIncomingAgentBinding`. It pre-fills
 * and waits; nothing is sent until the operator presses send.
 */
export function articlePostPrompt(input: { title: string; url: string }): string {
  const title = input.title.trim().replace(/"/g, "'");
  return [
    `/repurpose ${input.url}`,
    `Write one LinkedIn post for our company page from our article "${title}".`,
    'Use only facts, figures and statute references that appear in the article, and link to it.',
    'Keep it as a draft so I can schedule it.',
  ].join(' ');
}

export interface CalendarMomentPrefill {
  title: string;
  url: string;
  /** "YYYY-MM-DD", or "" when the article has no date yet. */
  startsOn: string;
  /** The moment's Type. `null` is "not set", a real answer (see CalendarMomentDialog). */
  contentType: DeskContentType | null;
}

/**
 * What an article contributes to a calendar moment: its title, its public link
 * and the day it went (or is due to go) live. Published beats scheduled because
 * it is the fact; a scheduled date is a plan. No date is a real answer — the
 * dialog saves it undated and says so.
 */
export function articleMomentPrefill(input: {
  title: string;
  publishedUrl: string | null;
  publishedAt: string | null;
  scheduledAt: string | null;
}): CalendarMomentPrefill {
  return {
    title: input.title.trim(),
    url: publicUrl(input.publishedUrl) ?? '',
    startsOn: isoDay(input.publishedAt) ?? isoDay(input.scheduledAt) ?? '',
    contentType: 'article' as const,
  };
}

function isoDay(value: string | null): string | null {
  if (!value) return null;
  const match = /^(\d{4}-\d{2}-\d{2})/.exec(value);
  return match ? match[1] : null;
}
